import * as React from "react";
import { IconSquare, IconSquareFilled } from "@tabler/icons-react";

import { cn } from "../lib/utils";

/** Filled-square color carried over from the original Material Rating. */
const FILLED_COLOR = "rgb(230, 200, 50)";
/** Hover-preview color carried over from the original Material Rating. */
const HOVER_COLOR = "#ff3d47";

export interface RatingProps {
  /** Current rating, in [0, max]. Non-finite values are treated as 0. */
  value: number;
  /** Number of squares. */
  max?: number;
  /** Step used for interactive clicks and keyboard adjustment. */
  precision?: 0.5 | 1;
  /** When true, the control is display-only (no pointer/keyboard interaction). */
  readOnly?: boolean;
  /** Called with the new value when the user picks a rating (interactive only). */
  onChange?: (value: number) => void;
  /** Pixel size of each square. */
  size?: number;
  /** Accessible label. Falls back to a generated "N out of M" description. */
  "aria-label"?: string;
  className?: string;
}

/** One square: an outlined base with a filled square clipped to `fraction` on top. */
function RatingSquare({
  fraction,
  color,
  size,
}: {
  fraction: number;
  color: string;
  size: number;
}) {
  return (
    <span
      aria-hidden="true"
      className="relative inline-flex shrink-0"
      style={{ width: size, height: size }}
    >
      <IconSquare size={size} className="text-muted-foreground/30 absolute inset-0" />
      <span
        data-testid="rating-fill"
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: `${fraction * 100}%` }}
      >
        <IconSquareFilled size={size} style={{ color }} />
      </span>
    </span>
  );
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

const Rating = React.forwardRef<HTMLSpanElement, RatingProps>(
  (
    {
      value,
      max = 5,
      precision = 1,
      readOnly = false,
      onChange,
      size = 20,
      className,
      "aria-label": ariaLabel,
    },
    ref
  ) => {
    const [hover, setHover] = React.useState<number | null>(null);

    const safeValue = Number.isFinite(value) ? clamp(value, 0, max) : 0;
    const displayValue = hover ?? safeValue;
    const previewing = hover != null;
    const label = ariaLabel ?? `${safeValue} out of ${max}`;

    if (readOnly) {
      return (
        <span
          ref={ref}
          data-slot="rating"
          role="img"
          aria-label={label}
          className={cn("inline-flex items-center gap-0.5", className)}
        >
          {Array.from({ length: max }, (_, i) => (
            <RatingSquare
              key={i}
              size={size}
              color={FILLED_COLOR}
              fraction={clamp(safeValue - i, 0, 1)}
            />
          ))}
        </span>
      );
    }

    const commit = (next: number) => {
      const clamped = clamp(next, 0, max);
      if (clamped !== safeValue) onChange?.(clamped);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
      switch (event.key) {
        case "ArrowRight":
        case "ArrowUp":
          event.preventDefault();
          commit(safeValue + precision);
          break;
        case "ArrowLeft":
        case "ArrowDown":
          event.preventDefault();
          commit(safeValue - precision);
          break;
        case "Home":
          event.preventDefault();
          commit(0);
          break;
        case "End":
          event.preventDefault();
          commit(max);
          break;
        default:
          break;
      }
    };

    // Pointer targets laid over each square. With half-step precision each square
    // splits into a left (i + 0.5) and right (i + 1) zone; otherwise one full zone.
    const zonesFor = (i: number) => {
      const steps = precision === 0.5 ? [i + 0.5, i + 1] : [i + 1];
      const zoneWidth = 100 / steps.length;
      return steps.map((zoneValue, step) => (
        <span
          key={zoneValue}
          data-testid="rating-zone"
          className="absolute inset-y-0 cursor-pointer"
          style={{ left: `${step * zoneWidth}%`, width: `${zoneWidth}%` }}
          onMouseEnter={() => setHover(zoneValue)}
          onClick={(event) => {
            // Rating is used inside clickable cards; don't trigger the card.
            event.stopPropagation();
            commit(zoneValue);
          }}
        />
      ));
    };

    return (
      <span
        ref={ref}
        data-slot="rating"
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={safeValue}
        aria-valuetext={`${safeValue} out of ${max}`}
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        onMouseLeave={() => setHover(null)}
        className={cn(
          "inline-flex items-center gap-0.5 outline-none",
          "focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:ring-2 rounded-md",
          className
        )}
      >
        {Array.from({ length: max }, (_, i) => (
          <span
            key={i}
            className="relative inline-flex shrink-0"
            style={{ width: size, height: size }}
          >
            <RatingSquare
              size={size}
              color={previewing ? HOVER_COLOR : FILLED_COLOR}
              fraction={clamp(displayValue - i, 0, 1)}
            />
            {zonesFor(i)}
          </span>
        ))}
      </span>
    );
  }
);
Rating.displayName = "Rating";

export { Rating };
